import { FC, useState } from 'react';

import { AnalyticsEvent, trackClick } from '@bonusly/analytics';
import { Box, Button, HStack, PlusOutline, Spinner, Text, useBreakpointValue, useDisclosure } from '@bonusly/design-system';
import { Permission, usePermission } from '@bonusly/who-am-i';

type AddAgendaItemProps = {
	onAddTopic: () => void;
	onAddGoal: () => void;
	isTopicDisabled?: boolean;
	isGoalDisabled?: boolean;
};

export const AddAgendaItem: FC<AddAgendaItemProps> = ({ onAddTopic, onAddGoal, isTopicDisabled = false, isGoalDisabled = false }) => {
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [isWorking, setIsWorking] = useState(false);
	const isMobile = useBreakpointValue({ base: true, sm: false });
	const isShowing = isMobile || isOpen;
	const canAccessGoals = usePermission(Permission.CanAccessGoals);

	if (isWorking) {
		return <Spinner size="sm" />;
	}

	return (
		<>
			<Box h="32px" data-component="add-agenda-item" onMouseEnter={onOpen} onMouseLeave={onClose} position="relative">
				<Button
					opacity={isShowing ? 0 : 1}
					pointerEvents={isShowing ? 'none' : 'auto'}
					onClick={onOpen}
					variant="subtle"
					position="absolute"
					left={0}
					maxW="32px"
					maxH="32px"
					minW="32px"
					minH="32px"
					aria-label="Add topic"
					transition="opacity 0.2s ease-in-out"
				>
					<PlusOutline h="20px" w="20px" />
				</Button>
				<HStack opacity={isShowing ? 1 : 0} spacing={1} bg="bg.surface" display="inline-flex" transition="opacity 0.2s ease-in-out">
					<Button
						variant="subtle"
						maxH="32px"
						paddingStart={3}
						paddingEnd={3}
						onClick={async () => {
							if (isWorking) return;
							setIsWorking(true);
							await onAddTopic();
							setIsWorking(false);
						}}
						isDisabled={isTopicDisabled}
						{...trackClick(AnalyticsEvent.OneOnOne.AgendaAddTopicClicked)}
					>
						{/* <ChatEmptyOutline color="text.softest" /> */}
						<Text fontSize="md" fontWeight="medium" ml={0} color="secondary.fg">
							Add topic
						</Text>
					</Button>
					{canAccessGoals && (
						<Button
							variant="subtle"
							maxH="32px"
							paddingStart={3}
							paddingEnd={3}
							onClick={async () => {
								if (isWorking) return;
								setIsWorking(true);
								await onAddGoal();
								setIsWorking(false);
							}}
							isDisabled={isGoalDisabled}
							{...trackClick(AnalyticsEvent.OneOnOne.AgendaAddGoalClicked)}
						>
							{/* <TargetOutline color="text.softest" /> */}
							<Text fontSize="md" fontWeight="medium" ml={0} color="secondary.fg">
								Add goal
							</Text>
						</Button>
					)}
				</HStack>
			</Box>
		</>
	);
};

AddAgendaItem.displayName = 'AddAgendaItem';
